import { DataType, FormOptions, FormV2, Input, Section, SelectOption } from '@globaleris/ui.library'
import {
  Box,
  Button,
  Card,
  Chip,
  makeStyles,
  Popover,
  Typography,
  useTheme
} from '@material-ui/core'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from '../../../helpers'
import { minimumOneFieldFilled } from '../../../helpers/validateFormHelper'
import { useIsFirstRender } from '../../../hooks/useIsFirstRender'
import { getCustomerDomain, storeCustomFilterForCurrentPage } from '../../../redux'
import { styles } from './customFilter.styles'

const MAX_RESULTS = 1000

export interface CustomFilterProps {
  // onApplyFilter: (appliedFilter: any) => void
  // onPrevalidateFilter?: (filterToApply: any) => Promise<boolean>
  onFetchData: (filterToApply: any) => Promise<any>
  onShowResults: (results: any[]) => void
  initialState?: any
  additionalFilters?: Input[]
  appliedFilter?: any
  enabled?: boolean
  subtitle?: string
  dateTypeSelectOptions?: SelectOption[]
  customInvalidFilterError?: string
  asPopover?: boolean
}

// Este Filtro es una v2.0 del CustomFilter. Referencia: US 63936

const CustomFilterV2: React.FC<CustomFilterProps> = ({
  // onApplyFilter,
  // onPrevalidateFilter,
  onFetchData,
  onShowResults,
  additionalFilters = [],
  appliedFilter = {},
  initialState = {},
  enabled = true,
  subtitle = undefined,
  dateTypeSelectOptions = null,
  customInvalidFilterError,
  asPopover = false
}) => {
  const isFirstRender = useIsFirstRender()
  const [t] = useTranslation(['translation', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [active, setActive] = React.useState<boolean>(false)
  const [formData, setFormData] = React.useState<any>(initialState)
  const [innerFormData, setInnerFormData] = React.useState<any>({
    ...initialState,
    ...appliedFilter
  })
  const innerFormDataRef = React.useRef<any>({ ...initialState, ...appliedFilter })
  const [showForm, setShowForm] = React.useState<boolean>(false)
  const [showInnerForm, setShowInnerForm] = React.useState<boolean>(true)
  const [searchButtonDisabled, setSearchButtonDisabled] = React.useState<boolean>(true)
  const [startDate, setStartDate] = React.useState<Date | undefined>(appliedFilter.startDate)
  const [endDate, setEndDate] = React.useState<Date | undefined>(appliedFilter.endDate)
  const [dateType, setDateType] = React.useState<number>(
    appliedFilter.dateFilterType ??
      (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
        ? dateTypeSelectOptions[0].value
        : undefined)
  )
  const [results, setResults] = React.useState<any[]>([])
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const formRef = React.useRef<any>()

  const tooManyResults = useMemo(() => {
    return results.length >= 1000
  }, [results])

  React.useEffect(() => {
    setStartDate(appliedFilter && appliedFilter.startDate)
    setEndDate(appliedFilter && appliedFilter.endDate)
    setDateType(
      (appliedFilter && appliedFilter.dateFilterType) ??
        (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
          ? dateTypeSelectOptions[0].value
          : undefined)
    )
    if (appliedFilter && isFirstRender) {
      const data = { ...initialState, ...appliedFilter }
      innerFormDataRef.current = data
      setInnerFormData(data)
    }
  }, [appliedFilter])

  interface formDependenciesProps {
    formState: any
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {
      dateFilterType:
        appliedFilter.dateFilterType ??
        (dateTypeSelectOptions && dateTypeSelectOptions.length > 0
          ? dateTypeSelectOptions[0].value
          : undefined)
    }
  })

  const invalidFilterError = (
    <div>
      <Typography variant="subtitle1" color="error">
        <ErrorOutlineIcon
          style={{
            marginRight: '2px',
            marginLeft: '-2px',
            marginTop: '-2px'
          }}
        />
        {customInvalidFilterError
          ? customInvalidFilterError
          : t('translation:components.searchFilter.invalidFilterError')}
      </Typography>
    </div>
  )

  const prevalidateSearch = async (currentFilter: any): Promise<void> => {
    // Lanza una única llamada con los campos rellenos y lanza la consulta con top 1000
    // Si la respuesta trae 1000 resultados: Mostramos mensajes dando la opción para ver los 1000 primeros o aumentar los filtros
    // Si la respuesta no trae 1000 resultados: Mostramos los resultados directamente
    //
    //
    //
    // if (!onPrevalidateFilter) return
    // if (!currentFilter.startDate || !currentFilter.endDate) return
    // setFormWaiting(true)
    // setFormMode(FormDialogMode.VIEW)
    // const filterIsValid = await onPrevalidateFilter(currentFilter)
    // console.log(currentFilter)
    // console.log(filterIsValid)
    // if (filterIsValid) {
    //   setFormMode(FormDialogMode.EDIT)
    //   setFormSubtitle('')
    // } else {
    //   setFormMode(FormDialogMode.VIEW)
    //   setFormSubtitle(invalidFilterError)
    // }
    // setFormWaiting(false)
    // return
  }

  const handleInitDateChange = async (newStartDate: any) => {
    if (!moment(newStartDate).isValid()) {
      return
    }
    const formData = {
      ...formDependencies.formState,
      startDate: newStartDate
    }
    await prevalidateSearch(formData)
    if (formData.endDate !== undefined && moment(formData.endDate).isBefore(moment(newStartDate))) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(newStartDate).toDate(),
          endDate: moment(newStartDate).toDate()
        }
      })
    } else {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(formData.startDate).toDate(),
          endDate: formData.endDate !== undefined ? moment(formData.endDate).toDate() : undefined
        }
      })
    }
  }

  const handleEndDateChange = async (newEndDate: any) => {
    if (
      !moment(newEndDate).isValid() ||
      (formDependencies.formState && formDependencies.formState.endDate === null)
    ) {
      return
    }
    const formData = {
      ...formDependencies.formState,
      endDate: newEndDate
    }
    await prevalidateSearch(formData)
    if (
      formData.startDate !== undefined &&
      moment(newEndDate).isBefore(moment(formData.startDate))
    ) {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          startDate: moment(formData.startDate).toDate(),
          endDate: moment(formData.endDate).toDate()
        }
      })
    } else {
      setFormDependencies({
        ...formDependencies,
        formState: {
          ...formData,
          endDate: moment(formData.endDate).toDate()
        }
      })
    }
  }

  const handleFilterInputChange = (
    newValueForInput: any,
    formData: any,
    onChange?: (value: string | number, formState: object) => void
  ) => {
    // if the input has an onChange handler, we call first this handler
    onChange && onChange(newValueForInput, formData)
    //
    prevalidateSearch(formData)
  }

  // Ref always points to the latest additionalFilters without triggering useMemo
  const additionalFiltersRef = React.useRef<Input[]>(additionalFilters)
  additionalFiltersRef.current = additionalFilters
  // Key based on structure (name+type+selectOptions length) — stable across re-renders that recreate
  // the array reference, but updates when async selectOptions finish loading
  const additionalFiltersKey = additionalFilters
    .map(f => `${f.name}:${f.type}:${f.selectOptions?.length ?? 0}`)
    .join(',')

  const searchFormDefinition: Section[] = React.useMemo<Section[]>(() => {
    let filters: Section[] = [
      {
        title: 'Buscador',
        inputs: [
          {
            name: 'startDate',
            label: t('translation:components.searchFilterV2.inputs.startDate'),
            type: DataType.DATE,
            clearable: true,
            onChange: (value, _formState) => {
              if (moment(value).isValid()) {
                checkFormData({ ...innerFormDataRef.current, startDate: moment(value).toDate() })
              }
              if (!value) {
                checkFormData({ ...innerFormDataRef.current, startDate: undefined })
              }
            }
            // onBlur: handleInitDateChange
          },
          {
            name: 'endDate',
            label: t('translation:components.searchFilterV2.inputs.endDate'),
            type: DataType.DATE,
            clearable: true,
            onChange: (value, _formState) => {
              if (moment(value).isValid()) {
                checkFormData({ ...innerFormDataRef.current, endDate: moment(value).toDate() })
              }
              if (!value) {
                checkFormData({ ...innerFormDataRef.current, endDate: undefined })
              }
            },
            minDate: formDependencies.formState?.startDate
              ? moment(formDependencies.formState.startDate).toISOString()
              : undefined
            // onBlur: handleEndDateChange
          }
        ]
      }
    ]

    let additionalFiltersList: Input[] = []
    if (additionalFiltersRef.current.length > 0) {
      additionalFiltersList = additionalFiltersRef.current.map((inputFilter, index) => ({
        ...inputFilter,
        onChange: (value, formState) => {
          // Access latest handler via ref to avoid stale closures
          additionalFiltersRef.current[index]?.onChange?.(value, formState)
          checkFormData({ ...innerFormDataRef.current, [inputFilter.name]: value })
        },
        onClear: () => {
          additionalFiltersRef.current[index]?.onClear?.()
          checkFormData({ ...innerFormDataRef.current, [inputFilter.name]: undefined })
        }
      }))
    }

    filters[0].inputs.push(...additionalFiltersList)

    return filters

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDependencies, additionalFiltersKey])

  const handleChipClick = (event: React.MouseEvent<HTMLElement>) => {
    // Sync innerFormData from ref so FormV2 mounts with the latest values
    // (ref always has current data; state may be stale since we stopped updating it on every onChange)
    setInnerFormData(innerFormDataRef.current)
    if (asPopover) {
      setAnchorEl(event.currentTarget)
    }
    setShowForm(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setShowForm(false)
  }

  const toggleShowFilters = () => {
    if (asPopover) {
      handleClose()
    } else {
      setShowForm(prevState => !prevState)
    }
  }

  const clearForm = () => {
    // TODO: implement a "clearForm" callback in the <Form /> component
    formRef.current.clear(initialState)
    innerFormDataRef.current = initialState
    setInnerFormData(initialState)
    setSearchButtonDisabled(true)
  }

  const checkFormData = (formData: any) => {
    const newData = formData ?? innerFormDataRef.current
    innerFormDataRef.current = newData
    if (!minimumOneFieldFilled(searchFormDefinition, newData)) {
      setSearchButtonDisabled(true)
    } else {
      setSearchButtonDisabled(false)
    }
    // NOTE: setInnerFormData is intentionally NOT called here.
    // FormV2 manages its own display state internally (same pattern as library examples).
    // Pushing data back on every onChange triggers FormV2's useEffect which resets all Text fields.
    // innerFormData is only updated on clear, appliedFilter load, and when the panel opens (handleChipClick).
  }

  const handleOnClickSearch = async () => {
    await fetchData()
  }

  const handleOnClickShowResults = () => {
    setActive(true)
    setResults([])
    storeCustomFilterForCurrentPage(innerFormDataRef.current)
    onShowResults(results)
  }

  const fetchData = async () => {
    setSearchButtonDisabled(true)
    setResults([])
    const response = await onFetchData(innerFormDataRef.current).then(response => {
      setSearchButtonDisabled(false)
      return response
    })

    if (response != null) {
      if (response.length >= 1000) {
        setResults(response)
      } else {
        // mostrar los resultados directamente
        setActive(true)
        setResults([])
        storeCustomFilterForCurrentPage(innerFormDataRef.current)
        onShowResults(response)
      }
    } else {
      // error
    }
  }

  //#region dataTableOptions
  const options: FormOptions = React.useMemo(() => {
    return {
      elevation: 0,
      columns: asPopover ? 2 : 3,
      analytics: {
        customer: getCustomerDomain(),
        view: t('translation:components.customFilterV2.analytics')
      },
      readOnly: false
    }
  }, [searchFormDefinition, asPopover])

  const formContent = (
    <>
      <FormV2
        ref={formRef}
        title={'Buscador'}
        sections={searchFormDefinition}
        data={innerFormData}
        options={options}
      />
      {tooManyResults && (
        <div className={classes.resultsContainer}>
          <Typography variant="subtitle1" color="secondary">
            {t('translation:components.searchFilterV2.tooManyResults')}
          </Typography>
        </div>
      )}

      <div className={classes.actionsContainer}>
        {tooManyResults && (
          <Button size="small" color="primary" onClick={handleOnClickShowResults}>
            {t('translation:components.searchFilterV2.actions.showResults')}
          </Button>
        )}
        <Button size="small" color="primary" onClick={toggleShowFilters}>
          {t('translation:components.searchFilterV2.actions.close')}
        </Button>

        <Button size="small" color="primary" onClick={clearForm}>
          {t('translation:components.searchFilterV2.actions.clearForm')}
        </Button>

        <Button
          size="small"
          variant="contained"
          color="primary"
          disabled={searchButtonDisabled}
          onClick={handleOnClickSearch}
        >
          {t('translation:components.searchFilterV2.actions.search')}
        </Button>
      </div>
    </>
  )

  return (
    <>
      {(asPopover || !showForm) && (
        <div className={classes.filters}>
          <Chip
            size="small"
            variant={enabled ? 'default' : 'outlined'}
            icon={<SearchIcon></SearchIcon>}
            clickable={enabled}
            className={clsx(classes.filter, { [classes.filterDisabled]: !enabled })}
            label={`${t('translation:components.searchFilterV2.chipLabel')}`}
            color={isEmpty(appliedFilter) ? 'default' : 'primary'}
            onClick={handleChipClick}
          />
        </div>
      )}

      {asPopover ? (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            style: { width: 560, marginRight: currentTheme.spacing(2), overflow: 'visible' }
          }}
        >
          <Box className={classes.formWithOutToolbar}>{formContent}</Box>
        </Popover>
      ) : (
        <Card className={clsx(classes.formWithOutToolbar, { [classes.formHidden]: !showForm })}>
          {formContent}
        </Card>
      )}
    </>
  )
}

export default React.memo(CustomFilterV2)
